import {
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  Stack,
  Button,
  useColorMode,
  useColorStyle,
  Space,
  Flex,
  Checkbox,
  Link,
  Grid,
} from '@tonic-one/react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigateWithHandler } from '@Utils/hooks';
import { ReauthRequiredOrg } from '@Types/provision';
import { isInUIC } from '@Utils/common';

interface Props {
  reauthRequiredOrgs: ReauthRequiredOrg[];
  onClose: () => void;
}

function AcsRetirementModal({ reauthRequiredOrgs, onClose }: Props) {
  const navigate = useNavigateWithHandler();
  const { t } = useTranslation('header', {
    keyPrefix: 'header.acsRetirement.modal',
  });
  const { t: tCommon } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [isOpen] = useState(true);
  const [acceptRisk, setAcceptRisk] = useState(false);

  const productName = !isInUIC()
    ? tCommon('common.nativeApp.productName')
    : tCommon('common.productName.CAS');

  const affectedServices = Array.from(
    new Set(
      reauthRequiredOrgs.flatMap((org) =>
        org.serviceName.split(', ').map((s) => s.trim())
      )
    )
  )
    .map((serviceName) => {
      const serviceKey =
        serviceName.charAt(0).toLowerCase() + serviceName.slice(1);
      return tCommon(`common.service.${serviceKey}`, serviceName);
    })
    .join(', ');

  const displayOrgs = reauthRequiredOrgs.slice(0, 2);
  const hasMore = reauthRequiredOrgs.length > 2;

  const handleRecreateToken = () => {
    // useNavigateWithHandler automatically handles both iframe and normal navigation
    navigate('/administration/service-account');
  };

  const handleNotNow = () => {
    if (!acceptRisk) {
      return;
    }
    onClose();
  };

  return (
    <Modal
      id="acsRetirementPopup"
      isOpen={isOpen}
      size="md"
      zIndex={1501}
      color={colorStyle.color.primary}
    >
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent>
        <ModalBody
          display="flex"
          flexDirection="column"
          alignItems="center"
          m="0 !important"
          p="32px"
        >
          {/* Title */}
          <Text size="2xl" fontWeight="semibold">
            {t('title')}
          </Text>
          <Space height="32px" />

          {/* Main Content */}
          <Stack spacing="24px" width="100%">
            {/* Sub Title */}
            <Text
              fontSize="16px"
              fontWeight={600}
              textAlign="center"
              lineHeight="1.375"
              mt="24px"
            >
              <Trans
                i18nKey="header.acsRetirement.modal.subTitle"
                values={{ services: affectedServices }}
              >
                placeholder
                <span style={{ color: colorStyle.color.primary }}>
                  placeholder
                </span>
                placeholder
              </Trans>
            </Text>

            {/* Why Section */}
            <Stack spacing="6px">
              <Text fontWeight="semibold">{t('why.title')}</Text>
              <Text
                fontSize="14px"
                color={colorStyle.color.secondary}
                lineHeight="1.43"
              >
                {t('why.content', { productName })}
              </Text>

              {/* Affected Organizations */}
              <Stack spacing="4px" mt="8px">
                <Text fontSize="14px" color={colorStyle.color.secondary}>
                  {t('why.affectedOrgs')}
                </Text>
                {displayOrgs.map((org, index) => (
                  <Text
                    key={index}
                    fontSize="14px"
                    color={colorStyle.color.secondary}
                    pl="16px"
                  >
                    • {org.orgName}
                  </Text>
                ))}
                {hasMore && (
                  <Text fontSize="14px" color={colorStyle.color.secondary}>
                    {t('why.checkEmail')}
                  </Text>
                )}
              </Stack>
            </Stack>

            {/* How Section */}
            <Stack spacing="6px">
              <Text fontWeight="semibold">{t('how.title')}</Text>
              <Grid templateColumns="20px 1fr" gap="2px">
                <Text color={colorStyle.color.secondary}>1.</Text>
                <Text
                  fontSize="14px"
                  color={colorStyle.color.secondary}
                  lineHeight="1.43"
                >
                  <Trans i18nKey="header.acsRetirement.modal.how.step1">
                    placeholder
                    <strong>placeholder</strong>
                    placeholder
                  </Trans>
                </Text>
                <Text color={colorStyle.color.secondary}>2.</Text>
                <Text
                  fontSize="14px"
                  color={colorStyle.color.secondary}
                  lineHeight="1.43"
                >
                  <Trans
                    i18nKey="header.acsRetirement.modal.how.step2"
                    values={{ services: affectedServices }}
                  >
                    placeholder
                    <strong>placeholder</strong>
                    placeholder
                  </Trans>
                </Text>
              </Grid>
            </Stack>
          </Stack>

          {/* Primary Button */}
          <Button
            py="12px"
            px="60px"
            size="lg"
            border="none"
            variant="primary"
            alignSelf="stretch"
            onClick={handleRecreateToken}
            mt="24px"
            mb="24px"
          >
            {t('button.recreate')}
          </Button>

          {/* Divider */}
          <Flex
            width="100%"
            height="0"
            style={{
              borderTop: `1px dashed rgba(255, 255, 255, 0.12)`,
            }}
          />

          {/* Not Now Section */}
          <Stack spacing="16px" width="100%" alignItems="center" mt="16px">
            <Link
              color="#6F9BF4"
              fontSize="14px"
              cursor="pointer"
              onClick={handleNotNow}
              opacity={acceptRisk ? 1 : 0.5}
              pointerEvents={acceptRisk ? 'auto' : 'none'}
            >
              {t('button.notNow')}
            </Link>

            <Flex width="100%" gap="8px" alignItems="flex-start">
              <Checkbox
                checked={acceptRisk}
                onChange={(e) => setAcceptRisk(e.target.checked)}
              />
              <Text fontSize="14px" color={colorStyle.color.primary}>
                {t('notNow.disclaimer')}
              </Text>
            </Flex>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default AcsRetirementModal;
